'use client'

import { useState, useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { CacheProvider } from '@chakra-ui/next-js'
import { ChakraProvider } from '@chakra-ui/react'
import { theme } from '@/theme'
import { AuthProvider } from '@/contexts/AuthContext'
import { RolePerspectiveProvider } from '@/contexts/RolePerspectiveContext'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { defaultQueryClientOptions } from '@/lib/queryCache'
import { reportError } from '@/lib/errors'
import { attachConnectivityToQueryClient } from '@/lib/connectivity/reactQuery'
import ConnectivitySync from '@/components/connectivity/ConnectivitySync'
import { registerUnhandledRejectionHandler } from '@/lib/unhandledRejection'

function attachGlobalErrorHandler(client: QueryClient): void {
  const queryCache = client.getQueryCache()
  const mutationCache = client.getMutationCache()

  queryCache.config.onError = (error) => {
    reportError(error, { context: { source: 'globalQueryCache' } })
  }

  mutationCache.config.onError = (error, _variables, _context, mutation) => {
    reportError(error, {
      context: {
        source: 'globalMutationCache',
        mutationKey: mutation.options.mutationKey?.join('.') ?? null,
      },
    })
  }
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => {
    const client = new QueryClient({
      defaultOptions: defaultQueryClientOptions,
    })
    attachConnectivityToQueryClient(client)
    attachGlobalErrorHandler(client)
    return client
  })

  // Register once on mount — idempotent across hot reloads
  useEffect(() => {
    registerUnhandledRejectionHandler()
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <ConnectivitySync />
      <CacheProvider>
        <ChakraProvider theme={theme}>
          <AuthProvider>
            {/*
              Do not key RolePerspectiveProvider on user id. Remounting the whole
              tree on sign-in unmounts /login mid-router.replace and leaves a stuck
              spinner. Perspective state resets in-place when the user changes.
            */}
            <RolePerspectiveProvider>
              <ErrorBoundary>{children}</ErrorBoundary>
            </RolePerspectiveProvider>
          </AuthProvider>
        </ChakraProvider>
      </CacheProvider>
    </QueryClientProvider>
  )
}
